import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Check, X, Loader2, Send, Settings } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import { runJob } from "@/lib/pollJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendNowPage() {
  const { token, newsId } = useParams();
  const navigate = useNavigate();
  const isNews = Boolean(newsId);
  const [status, setStatus] = useState("sending"); // sending | sent | error
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return; // guard React-StrictMode double-invocation
    firedRef.current = true;
    (async () => {
      try {
        const r = isNews
          ? await runJob(`${API}/manage/${token}/news/${newsId}/send-now/start`, undefined, { timeoutMs: 240000 })
          : (await axios.post(`${API}/manage/${token}/send-now`)).data;
        setResult(r);
        setStatus("sent");
      } catch (e) {
        setMessage(e?.response?.data?.detail || "Couldn't send right now.");
        setStatus("error");
      }
    })();
  }, [token, newsId, isNews]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-2xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-black bg-white p-10" data-testid="sendnow-card">
          {status === "sending" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
              <div className="ticker-label mb-3">Dispatching</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">{isNews ? "Preparing your news…" : "Preparing your forecast…"}</h1>
              <p className="mt-4 text-neutral-700">{isNews ? "Searching the live web for fresh stories on your topics. Takes a few seconds." : "Pulling from 5 models and writing a summary. Takes a few seconds."}</p>
            </>
          )}
          {status === "sent" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-[#FFD600] mb-6">
                <Check className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">On its way</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">
                {isNews ? "News sent." : (result?.sent > 1 ? `${result.sent} forecasts sent.` : "Forecast sent.")}
              </h1>
              <p className="mt-6 text-neutral-700">
                Check your inbox in a few seconds. If you don&rsquo;t see it, check your spam/promotions folder and mark it
                &ldquo;Not spam&rdquo; so future forecasts land in your inbox.
              </p>
              <button
                onClick={() => navigate(`/manage/${token}`)}
                data-testid="sendnow-manage-button"
                className="btn-brutal mt-8 inline-flex items-center gap-3 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold"
              >
                <Settings className="w-4 h-4" /> Manage my subscriptions
              </button>
            </>
          )}
          {status === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <X className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">Hmm</div>
              <h1 className="font-display text-3xl font-black tracking-tighter">We couldn&rsquo;t send that just now.</h1>
              <p className="mt-4 text-neutral-700">{message}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => { firedRef.current = false; setStatus("sending"); }}
                  data-testid="sendnow-retry-button"
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold"
                >
                  <Send className="w-4 h-4" /> Try again
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
                >
                  Back to home
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
